/**
 * The Git tab's small dialogs (GitTab.jsx): Commit (a message and what changed, that service only), Tag (name, optional
 * message, "also push"), New branch, and Remotes (Flow itself read-only, the external ones with Open and Remove, add one
 * with the connected account for its host, from AccountsService). Errors in words; one about the person's git account
 * offers "Connect account" (GitErrorText).
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Dropdown, Input, Modal, Select } from 'antd';
import { ArrowSquareOut, CaretDown, Trash } from '@phosphor-icons/react';
import {
  addRemote, connectHref, fetchRemote, listTags, needsAccount, remoteTags, removeRemote,
} from '../../services/GitService';
import { listAccounts } from '../../services/AccountsService';
import { accountsForHost, parseRepoUrl } from '../../utils/gitRepoSource';
import {
  changedCount, flowCloneUrl, refNameProblem, remoteNameProblem, remoteWebUrl, remotesOf, tagRows,
} from './gitModel';

/** An error's words, with "Connect account" when it is about the person's git account. */
export function GitErrorText({ error, className = 'git-error' }) {
  if (!error) { return null; }
  return (
    <p className={className} role="alert">
      {error.message || String(error)}
      {needsAccount(error) && (
        <>
          {' '}
          <a data-testid="git-connect-account" href={connectHref(error)} rel="noreferrer" target="_blank">Connect account</a>
        </>
      )}
    </p>
  );
}

GitErrorText.propTypes = {
  className: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

/** Runs `action`, keeps it busy meanwhile and its error after; resolves true when it worked. */
function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async(action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

export function CommitModal({ repo, onCancel, onCommit }) {
  const [message, setMessage] = useState('');
  const { busy, error, run } = useSubmit();
  const files = Array.isArray(repo.files) ? repo.files : [];
  const count = changedCount(repo);
  const submit = async() => {
    if (!message.trim()) { return; }
    if (await run(() => onCommit(message.trim()))) { onCancel(); }
  };
  return (
    <Modal
      destroyOnHidden
      okButtonProps={{ disabled: !message.trim(), loading: busy, 'data-testid': 'git-commit-submit' }}
      okText="Commit"
      onCancel={onCancel}
      onOk={submit}
      open
      title={`Commit ${repo.service}`}
      width={520}
    >
      <div className="git-dialog" data-testid="git-commit-modal">
        <label className="git-field">
          <span>Message</span>
          <Input.TextArea
            aria-label="Commit message"
            autoFocus
            autoSize={{ minRows: 2, maxRows: 6 }}
            onChange={(e) => setMessage(e.target.value)}
            onPressEnter={(e) => { if (e.metaKey || e.ctrlKey) { submit(); } }}
            placeholder="What changed, in a few words"
            value={message}
          />
        </label>
        <p className="git-muted">
          {count === 0 ? 'Nothing changed: the commit would be empty.' : `${count} changed ${count === 1 ? 'file' : 'files'}, all included.`}
        </p>
        {files.length > 0 && (
          <ul className="git-files" data-testid="git-commit-files">
            {files.slice(0, 50).map((file) => (
              <li key={file.path}><span className="git-file-status">{file.status || 'M'}</span><span className="git-mono">{file.path}</span></li>
            ))}
            {files.length > 50 && <li className="git-muted">{`and ${files.length - 50} more`}</li>}
          </ul>
        )}
        <GitErrorText error={error} />
      </div>
    </Modal>
  );
}

CommitModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  repo: PropTypes.object.isRequired,
};

export function TagModal({ repo, onCancel, onTag }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const hasRemote = remotesOf(repo).length > 0;
  const [alsoPush, setAlsoPush] = useState(hasRemote);
  const [touched, setTouched] = useState(false);
  const { busy, error, run } = useSubmit();
  const problem = refNameProblem(name, Array.isArray(repo.tags) ? repo.tags.map((t) => (typeof t === 'string' ? t : t?.name)) : []);
  const submit = async() => {
    setTouched(true);
    if (problem) { return; }
    if (await run(() => onTag({ name: name.trim(), message: message.trim(), push: hasRemote && alsoPush }))) { onCancel(); }
  };
  return (
    <Modal
      destroyOnHidden
      okButtonProps={{ loading: busy, 'data-testid': 'git-tag-submit' }}
      okText="Create tag"
      onCancel={onCancel}
      onOk={submit}
      open
      title={`Tag ${repo.service}`}
      width={460}
    >
      <div className="git-dialog" data-testid="git-tag-modal">
        <label className="git-field">
          <span>Name</span>
          <Input aria-label="Tag name" autoFocus onChange={(e) => setName(e.target.value)} onPressEnter={submit} placeholder="v1.0.0" status={touched && problem ? 'error' : undefined} value={name} />
        </label>
        {touched && problem && <p className="git-error" role="alert">{problem}</p>}
        <label className="git-field">
          <span>Message <em className="git-muted">(optional: an annotated tag)</em></span>
          <Input aria-label="Tag message" onChange={(e) => setMessage(e.target.value)} value={message} />
        </label>
        <p className="git-muted">{`On ${repo.branch || 'the current branch'}${repo.lastCommit?.sha ? ` at ${String(repo.lastCommit.sha).slice(0, 7)}` : ''}.`}</p>
        {hasRemote && <Checkbox checked={alsoPush} onChange={(e) => setAlsoPush(e.target.checked)}>Also push the tag</Checkbox>}
        <GitErrorText error={error} />
      </div>
    </Modal>
  );
}

TagModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onTag: PropTypes.func.isRequired,
  repo: PropTypes.object.isRequired,
};

const TAG_STATE_TEXT = {
  synced: (row) => `on ${row.onRemotes.join(', ')}`,
  'only-here': () => 'only here, not pushed',
  'remote-only': (row) => `on ${row.onRemotes.join(', ')}, not fetched`,
};

/**
 * Tags of a service: the local ones and what each remote has (read when the person can fetch), "Pull tags" (fetch every
 * tag of a remote, never deleting a local one) and Check out (detached HEAD at the tag, said in words).
 */
export function TagsModal({ projectId, repo, canWrite, onCancel, onChanged, onCheckout }) {
  const remotes = remotesOf(repo);
  const [data, setData] = useState({ loading: true, local: [], sets: {}, notes: [] });
  const { busy, error, run } = useSubmit();
  const load = async() => {
    const notes = [];
    let local = [];
    try {
      local = (await listTags(projectId, repo.service)).tags || [];
    } catch (err) {
      notes.push(err.message);
    }
    const sets = {};
    if (canWrite) {
      for (const remote of remotes) {
        try {
          // eslint-disable-next-line no-await-in-loop -- one remote at a time: each asks the server's git
          const answer = await remoteTags(projectId, { service: repo.service, remote: remote.name });
          sets[remote.name] = new Set((answer.tags || []).map((t) => t.name));
        } catch (err) {
          notes.push(`Could not read the tags of ${remote.name}: ${err.message}`);
        }
      }
    }
    setData({ loading: false, local, sets, notes });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, on open
  const rows = useMemo(() => tagRows(data.local, data.sets), [data]);
  const pull = (names) => run(async() => {
    for (const remote of names) {
      // eslint-disable-next-line no-await-in-loop -- one remote at a time
      await fetchRemote(projectId, { service: repo.service, remote, tags: true });
    }
    await load();
    onChanged();
  });
  const pullItems = [...remotes.map((r) => ({ key: r.name, label: `Pull tags from ${r.name}` })), { type: 'divider' }, { key: '__all__', label: 'All remotes' }];
  return (
    <Modal destroyOnHidden footer={<Button onClick={onCancel}>Close</Button>} onCancel={onCancel} open title={`Tags of ${repo.service}`} width={560}>
      <div className="git-dialog" data-testid="git-tags-modal">
        {canWrite && remotes.length > 0 && (
          remotes.length === 1
            ? <Button data-testid="git-tags-pull" loading={busy} onClick={() => pull([remotes[0].name])} style={{ alignSelf: 'flex-start' }}>{`Pull tags from ${remotes[0].name}`}</Button>
            : (
              <Dropdown menu={{ items: pullItems, onClick: ({ key }) => pull(key === '__all__' ? remotes.map((r) => r.name) : [key]) }} trigger={['click']}>
                <Button data-testid="git-tags-pull" loading={busy} style={{ alignSelf: 'flex-start' }}>Pull tags <CaretDown aria-hidden size={12} /></Button>
              </Dropdown>
            )
        )}
        {data.loading && <p className="git-muted" role="status">Reading the tags…</p>}
        {!data.loading && rows.length === 0 && <p className="git-muted">No tags yet.</p>}
        <ul className="git-remotes" aria-label="Tags">
          {rows.map((row) => (
            <li className="git-remote" data-testid={`git-tag-row-${row.name}`} key={row.name}>
              <span className="git-remote-name" style={{ width: 'auto', minWidth: 96 }} title={row.sha || undefined}>{row.name}</span>
              <span className="git-remote-url git-muted">{TAG_STATE_TEXT[row.state] ? TAG_STATE_TEXT[row.state](row) : ''}</span>
              {canWrite && row.state !== 'remote-only' && (
                <Button data-testid={`git-tag-checkout-${row.name}`} onClick={() => onCheckout(row.name)} size="small" title="Detached HEAD at this tag: commits made there are on no branch">Check out</Button>
              )}
            </li>
          ))}
        </ul>
        {data.notes.map((note) => <p className="git-error" key={note} role="alert">{note}</p>)}
        <GitErrorText error={error} />
      </div>
    </Modal>
  );
}

TagsModal.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  repo: PropTypes.object.isRequired,
};

export function BranchModal({ repo, onCancel, onCreate }) {
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const { busy, error, run } = useSubmit();
  const problem = refNameProblem(name, Array.isArray(repo.branches) ? repo.branches : []);
  const submit = async() => {
    setTouched(true);
    if (problem) { return; }
    if (await run(() => onCreate(name.trim()))) { onCancel(); }
  };
  return (
    <Modal
      destroyOnHidden
      okButtonProps={{ loading: busy, 'data-testid': 'git-branch-submit' }}
      okText="Create and switch"
      onCancel={onCancel}
      onOk={submit}
      open
      title={`New branch in ${repo.service}`}
      width={440}
    >
      <div className="git-dialog" data-testid="git-branch-modal">
        <label className="git-field">
          <span>Name</span>
          <Input aria-label="Branch name" autoFocus onChange={(e) => setName(e.target.value)} onPressEnter={submit} placeholder="feature/login" status={touched && problem ? 'error' : undefined} value={name} />
        </label>
        {touched && problem && <p className="git-error" role="alert">{problem}</p>}
        <p className="git-muted">{`From ${repo.branch || 'the current branch'}, with your uncommitted changes.`}</p>
        <GitErrorText error={error} />
      </div>
    </Modal>
  );
}

BranchModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  repo: PropTypes.object.isRequired,
};

/** Remotes: Flow (read-only, not removable), the external ones, and adding one. `canWrite` false: the list only. */
export function RemotesModal({ projectId, repo, cloneBase = null, canWrite, onCancel, onChanged }) {
  const remotes = remotesOf(repo);
  const [name, setName] = useState(remotes.length ? '' : 'origin');
  const [url, setUrl] = useState('');
  const [account, setAccount] = useState('');
  const [touched, setTouched] = useState(false);
  const [accounts, setAccounts] = useState({ list: [], loaded: false });
  const { busy, error, run } = useSubmit();
  useEffect(() => {
    if (!canWrite) { return undefined; }
    let live = true;
    listAccounts()
      .then((answer) => { if (live) { setAccounts({ list: answer?.accounts || [], loaded: true }); } })
      .catch(() => { if (live) { setAccounts({ list: [], loaded: true }); } });
    return () => { live = false; };
  }, [canWrite]);
  const parsed = useMemo(() => parseRepoUrl(url), [url]);
  const forHost = parsed.error ? [] : accountsForHost(accounts.list, parsed.host);
  const nameProblem = remoteNameProblem(name, remotes.map((r) => r.name));
  const problem = nameProblem || (parsed.error ? parsed.error : null);
  const flowUrl = flowCloneUrl(cloneBase, repo.service);

  const add = async() => {
    setTouched(true);
    if (problem) { return; }
    const ok = await run(() => addRemote(projectId, { service: repo.service, name: name.trim(), url: url.trim(), account: account || undefined }));
    if (ok) {
      setName('');
      setUrl('');
      setAccount('');
      setTouched(false);
      onChanged();
    }
  };
  const remove = (remoteName) => run(async() => {
    await removeRemote(projectId, repo.service, remoteName);
    onChanged();
  });

  return (
    <Modal destroyOnHidden footer={<Button onClick={onCancel}>Close</Button>} onCancel={onCancel} open title={`Remotes of ${repo.service}`} width={600}>
      <div className="git-dialog" data-testid="git-remotes-modal">
        <ul className="git-remotes">
          <li className="git-remote is-flow" data-testid="git-remote-flow">
            <span className="git-remote-name">flow</span>
            <span className="git-mono git-remote-url">{flowUrl || 'Hosted by Flow'}</span>
            <span className="git-chip">Flow</span>
          </li>
          {remotes.map((remote) => {
            const web = remoteWebUrl(remote);
            return (
              <li className="git-remote" data-testid={`git-remote-${remote.name}`} key={remote.name}>
                <span className="git-remote-name">{remote.name}</span>
                <span className="git-mono git-remote-url" title={remote.url}>{remote.url}</span>
                {web && (
                  <a aria-label={`Open ${remote.name}`} className="git-icon-link" href={web} rel="noreferrer" target="_blank">
                    <ArrowSquareOut aria-hidden size={16} />
                  </a>
                )}
                {canWrite && (
                  <Button aria-label={`Remove ${remote.name}`} disabled={busy} icon={<Trash aria-hidden size={16} />} onClick={() => remove(remote.name)} size="small" type="text" />
                )}
              </li>
            );
          })}
        </ul>
        {canWrite && (
          <div className="git-remote-add" data-testid="git-remote-add">
            <strong>Add a remote</strong>
            <div className="git-row">
              <Input aria-label="Remote name" className="git-remote-add-name" onChange={(e) => setName(e.target.value)} placeholder="origin" value={name} />
              <Input aria-label="Remote URL" className="git-grow" onChange={(e) => { setUrl(e.target.value); setAccount(''); }} placeholder="https://gitlab.com/group/repo.git" value={url} />
            </div>
            {!parsed.error && forHost.length > 0 && (
              <Select
                aria-label="Account"
                className="git-remote-add-account"
                onChange={setAccount}
                options={[
                  { value: '', label: forHost[0].isDefault ? `Your default: ${forHost[0].label || forHost[0].name}` : 'No account (public repository)' },
                  ...forHost.map((a) => ({ value: a.id, label: a.name ? `${a.name} (${a.label || a.login})` : a.label || a.login })),
                ]}
                value={account}
              />
            )}
            {!parsed.error && forHost.length === 0 && accounts.loaded && (
              <p className="git-muted">No account for {parsed.host}: fine for a public repository. Private? <a href={connectHref(null)} rel="noreferrer" target="_blank">Connect one</a>.</p>
            )}
            {touched && problem && <p className="git-error" role="alert">{problem}</p>}
            <div><Button data-testid="git-remote-add-submit" loading={busy} onClick={add} type="primary">Add remote</Button></div>
          </div>
        )}
        <GitErrorText error={error} />
      </div>
    </Modal>
  );
}

RemotesModal.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  cloneBase: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  repo: PropTypes.object.isRequired,
};
